import axios from 'axios';
import { getTargetVerticalName } from '@/utils/url';
import { Order } from '@/models/order';
import { OrderAccess } from '@/models/orderAccess';
import { CreditCard } from '@/models/creditCard';
import { LOGOUT_REASON_KEY } from '@/consts';
import { Recurrence } from '@/models/recurrence';

export class BffService {
  constructor(host, router) {
    const requesterId = 'perfil/bff';

    const instance = {
      baseURL: host,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'x-requester-id': requesterId,
      },
    };

    this.client = new axios.create(instance);
    this.clientDownload = new axios.create({
      ...instance,
      responseType: 'blob',
    });
    this.clientWithTargetVertical = new axios.create({
      ...instance,
      headers: {
        'x-requester-id': requesterId,
        'x-vertical': getTargetVerticalName(),
      },
    });

    const onApiSuccess = response => response;
    const onApiError = resp => {
      const response = resp?.response;
      const data = response?.data;
      const status = response?.status;

      if (status === 401) {
        const tag = data?.error?.tag || '';

        if (tag === 'AUTH.USER_SESSION_EXPIRED') {
          const reason = data?.meta?.reason || '';
          sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
        }

        const partner = localStorage.getItem('partner@estrategia') || null;
        localStorage.clear();
        if (partner) {
          localStorage.setItem('partner@estrategia', partner);
        }
        // Pega todos os query params diretamente da URL atual e repassa para o logout
        const urlParams = new URLSearchParams(window.location.search);
        const query = {};
        for (const [key, value] of urlParams.entries()) {
          query[key] = value;
        }
        return router.push({
          name: 'logout',
          query,
        });
      }

      return Promise.reject(resp);
    };
    this.client.interceptors.response.use(onApiSuccess, onApiError);
    this.clientWithTargetVertical.interceptors.response.use(onApiSuccess, onApiError);
  }

  async getPurchaseHistory() {
    const orders = await this.get('/bff/profile/purchase-history');
    return orders.map(order => Order.fromJson(order));
  }

  async getFailedRecurrences() {
    const failedRecurrences = await this.get('/bff/profile/failed-recurrences');
    return failedRecurrences.map(r => Recurrence.fromJson(r));
  }

  /**
   * @param {string} orderNumber
   * @param {string} vertical
   * @returns {Promise<Order>}
   */
  async getOrderDetails(orderNumber, vertical) {
    const response = await this.get(`/bff/profile/order/${orderNumber}`, vertical);
    const order = Order.fromJson(response);
    return order;
  }

  /**
   * @param {string} orderNumber
   * @param {string} vertical
   * @param {object} [params] itemIds, horizontals, status, orderBy, sort, page, perPage
   * @returns {Promise<{ accesses: OrderAccess[], meta: object }>}
   */
  async getOrderAccesses(orderNumber, vertical, params = {}) {
    if (this._getOrderAccessesCancelToken) {
      this._getOrderAccessesCancelToken.cancel();
    }
    this._getOrderAccessesCancelToken = axios.CancelToken.source();

    const query = this.mountAccessSearchParams(params);
    const envelope = await this.getWithMeta(`/bff/profile/order/${orderNumber}/accesses${query}`, vertical, {
      cancelToken: this._getOrderAccessesCancelToken.token,
    });
    const accesses = (envelope?.data ?? []).map(access => OrderAccess.fromJson(access));
    return { accesses, meta: envelope?.meta ?? {} };
  }

  /**
   * @param {string} orderNumber
   * @param {string} vertical
   * @param {string} term
   * @returns {Promise<Array<{ item_id: string, name: string }>>}
   */
  async getOrderItems(orderNumber, vertical, term) {
    if (this._getOrderItemsCancelToken) {
      this._getOrderItemsCancelToken.cancel();
    }
    this._getOrderItemsCancelToken = axios.CancelToken.source();

    const query = term ? `?term=${encodeURIComponent(term)}` : '';
    return (await this.get(`/bff/profile/order/${orderNumber}/items${query}`, vertical, {
      cancelToken: this._getOrderItemsCancelToken.token,
    })) ?? [];
  }

  /**
   * @param {string} orderNumber
   * @param {string} vertical
   * @returns {Promise<Array<{ product_id: string, name: string }>>}
   */
  async getOrderProducts(orderNumber, vertical) {
    return (await this.get(`/bff/profile/order/${orderNumber}/products`, vertical)) ?? [];
  }

  /**
   * @param {string} vertical
   * @returns {Promise<Array<{ value: string, label: string }>>}
   */
  async getHorizontals(vertical) {
    const horizontals = (await this.get('/bff/ecommerce/horizontals', vertical)) ?? [];
    return horizontals.map(horizontal => ({ value: horizontal.slug, label: horizontal.name }));
  }

  mountAccessSearchParams({ itemIds = [], productIds = [], horizontals = [], status = '', orderBy = '', sort = '', page = 0, perPage = 0 } = {}) {
    const params = new URLSearchParams();
    itemIds.forEach(id => params.append('item_id', id));
    productIds.forEach(id => params.append('product_id', id));
    horizontals.forEach(h => params.append('horizontal', h));
    if (status) { params.set('status', status) }
    if (orderBy) { params.set('order_by', orderBy) }
    if (sort) { params.set('sort', sort) }
    if (page) { params.set('page', page) }
    if (perPage) { params.set('per_page', perPage) }
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  async getOrderNfe(isProduct, orderNumber, vertical, index = 0) {
    const params = new URLSearchParams({
      [isProduct ? 'is_nfe_product' : 'is_nfe_service']: 'true',
      index: String(index),
    });
    return await this.download(`/bff/ecommerce/orders/${orderNumber}/nfe?${params.toString()}`, vertical);
  }

  async getBonusesList(orderNumber, vertical) {
    const response = await this.get(`/bff/ecommerce/catalog/order/${orderNumber}`, vertical);
    return Order.toBonusList(response?.bonuses ?? []);
  }

  async get(url, vertical, config = {}) {
    const headers = { ...config.headers };
    if (vertical) { headers['x-vertical'] = vertical }
    return this.client.get(url, { ...config, headers }).then(resp => resp.data.data);
  }

  async getWithMeta(url, vertical, config = {}) {
    const headers = { ...config.headers };
    if (vertical) { headers['x-vertical'] = vertical }
    return this.client.get(url, { ...config, headers }).then(resp => resp.data);
  }

  async download(url, vertical ) {
    const headers = {};
    if (vertical) { headers['x-vertical'] = vertical }
    return this.clientDownload.get(url, { headers }).then(resp => resp.data);
  }

  /**
   * @returns {Promise<CreditCard[]>}
   */
  getCreditCards() {
    return this.get('/bff/profile/credit-cards').then(data => data.map(cc => CreditCard.fromJson(cc)));
  }

  createCreditCard(payload) {
    return this.client.post('/bff/profile/credit-cards', payload);
  }

  deleteCreditCard(cardId) {
    return this.client.delete('/bff/profile/credit-cards/'+cardId);
  }

  setUtm ({ source, medium, campaign }) {
    const correctParams = {
      utm_source: source,
      utm_medium: medium,
      utm_campaign: campaign,
    };

    return this.clientWithTargetVertical.post('/bff/ecommerce/utm', correctParams);
  }

  async getPartnerBySlug(slug) {
    return this.client.get(`/bff/company/${slug}`).then(resp => resp.data);
  }

  async createTrialAccessByGoalId(goalId) {
    return this.clientWithTargetVertical.post(`/bff/goals/userGoals/trial-access/${goalId}`);
  }

  async getUserDownloads(orderNumber, vertical, fromDate, toDate) {
    let query = '';
    if (fromDate) {
      query += `from_date=${fromDate}`;
    }
    if (toDate) {
      query += query ? `&to_date=${toDate}` : `to_date=${toDate}`;
    }
    if (query) {
      query = `?${query}`;
    }

    const url = `/bff/profile/downloads/${orderNumber}${query}`;

    return await this.get(url, vertical);
  }

  async createNFe (orderId, vertical) {
    const headers = {};
    if (vertical) { headers['x-vertical'] = vertical }
    return this.client.post('/bff/ecommerce/nfe', { order_id: orderId }, { headers }).then(resp => resp.data.data);
  }

  async getMissingUrl (nfeId, vertical) {
    const headers = {};
    if (vertical) { headers['x-vertical'] = vertical }
    const url = `/bff/ecommerce/nfe/${nfeId}/get-missing-url`;
    return this.client.post(url, null, { headers }).then(resp => resp.data.data);
  }
}
